import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useShell } from "../../shell/context/ShellContext";
import { UserManagement } from "../../components/SettingsPanel/UserManagement";
import { DocumentUploadsTab } from "../../components/DocumentUploads/DocumentUploadsTab";
import { DocumentArrowUp20Regular, People20Regular, Settings20Regular } from "@fluentui/react-icons";
import styles from "./Settings.module.css";

const TABS = [
  { key: "general", label: "General", icon: <Settings20Regular /> },
  { key: "users", label: "Users & Access", icon: <People20Regular /> },
  { key: "documents", label: "Manual Document Ingestion", icon: <DocumentArrowUp20Regular /> },
] as const;

type TabKey = (typeof TABS)[number]["key"];

export default function Settings() {
  const { isAdmin, roleLoaded, userInfo } = useShell();
  const navigate = useNavigate();
  const currentUserId = userInfo?.clientPrincipal?.userId ?? "";
  const [activeTab, setActiveTab] = useState<TabKey>("general");

  useEffect(() => {
    if (roleLoaded && !isAdmin) {
      navigate("/", { replace: true });
    }
  }, [roleLoaded, isAdmin, navigate]);

  if (!roleLoaded) return null;
  if (!isAdmin) return null;

  return (
    <div className={styles.settingsPage}>
      <div className={styles.settingsHeader}>
        <h1 className={styles.settingsTitle}>Settings</h1>
        <p className={styles.settingsSubtitle}>Manage your global application preferences.</p>
      </div>

      <nav className={styles.tabBar}>
        {TABS.map((tab) => (
          <button
            key={tab.key}
            className={`${styles.tab} ${activeTab === tab.key ? styles.tabActive : ""}`}
            onClick={() => setActiveTab(tab.key)}
          >
            <span className={styles.tabIcon}>{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </nav>

      <div className={styles.tabContent}>
        {activeTab === "users" && (
          <UserManagement currentUserId={currentUserId} />
        )}
        {activeTab === "general" && (
          <div className={styles.placeholderPane}>
            <Settings20Regular className={styles.placeholderIcon} />
            <p className={styles.placeholderText}>
              General settings will appear here in a future update.
            </p>
          </div>
        )}
        {activeTab === "documents" && <DocumentUploadsTab />}
      </div>
    </div>
  );
}
